// Three-column layout: rules / guess / ledger. Cord-pull banner
// overlays the whole shell when an andon event fires. Reset button
// in the header so the demo is replayable without dropping to console.

import { reset } from "./lib/andon-loop";
import { RulesPanel } from "./components/RulesPanel";
import { GuessPanel } from "./components/GuessPanel";
import { LedgerPanel } from "./components/LedgerPanel";
import { AndonBanner } from "./components/AndonBanner";
import { AiThinkingPanel, StatusBadge } from "./components/AiThinkingPanel";

export default function App() {
  return (
    <div className="app">
      <AndonBanner />
      <header className="app-head">
        <div className="app-brandnav">
          <a
            className="app-home"
            href="https://www.binary-blender.com/"
            title="binary-blender.com"
          >
            <span className="app-home-mark">◆</span>
            Binary&nbsp;Blender
          </a>
          <a
            className="app-home"
            href="https://www.binary-blender.com/apps"
            title="all apps"
          >
            apps
          </a>
        </div>
        <div className="app-mark">
          <span className="app-cord">⚠</span>
          <span className="app-title">ANDON&nbsp;LOOP</span>
          <span className="app-sub">password demo · agicore continual harness</span>
        </div>
        <div className="app-actions">
          <StatusBadge />
          <button
            type="button"
            className="app-home"
            onClick={() => { if (confirm("Reset to the starter ruleset? Wipes ledger + guesses.")) reset(); }}
            title="Reset to starter ruleset"
          >
            reset
          </button>
        </div>
      </header>

      <main className="app-grid">
        <div className="app-col"><RulesPanel /></div>
        <div className="app-col"><GuessPanel /></div>
        <div className="app-col"><LedgerPanel /></div>
      </main>

      <AiThinkingPanel />
      <footer className="app-foot">
        <span>
          watch how the deterministic system handles the AI's proposals: every
          row in the ledger is a real lifecycle transition through a tiered
          mutation pipeline. console interface available as <code>window.demo</code>.
        </span>
      </footer>

      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .app {
        height: 100vh;
        display: flex;
        flex-direction: column;
      }
      .app-head {
        display: flex; align-items: center; gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-1);
        border-bottom: 3px solid var(--border);
      }
      .app-brandnav {
        display: flex; align-items: center; gap: var(--space-2);
      }
      .app-home {
        display: inline-flex; align-items: center; gap: 7px;
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 13px;
        letter-spacing: 0.04em;
        color: #fff;
        text-decoration: none;
        padding: 6px 14px;
        border: 2px solid var(--blueprint);
        border-radius: var(--radius-1);
        background: var(--blueprint);
        box-shadow: 2px 2px 0 var(--fg-0);
        transition: transform 0.08s ease, box-shadow 0.08s ease;
      }
      .app-home:hover {
        transform: translate(-1px, -1px);
        box-shadow: 3px 3px 0 var(--fg-0);
      }
      .app-home:active {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px 0 var(--fg-0);
      }
      .app-home-mark {
        color: var(--caution-yellow);
        font-size: 12px;
      }
      .app-mark {
        flex: 1;
        display: flex; align-items: baseline; gap: var(--space-3);
      }
      .app-cord {
        font-size: 20px;
        color: var(--andon-red);
      }
      .app-title {
        font-family: var(--font-display);
        font-weight: 900;
        font-size: 18px;
        letter-spacing: 0.06em;
        color: var(--fg-0);
      }
      .app-sub {
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--fg-2);
      }
      .app-actions {
        display: flex; gap: var(--space-2); align-items: center;
      }
      .app-grid {
        flex: 1;
        display: grid;
        grid-template-columns: minmax(280px, 1fr) minmax(360px, 1.4fr) minmax(320px, 1.2fr);
        gap: var(--space-3);
        padding: var(--space-3);
        min-height: 0;
      }
      .app-col {
        min-height: 0;
        min-width: 0;
        display: flex;
      }
      .app-col > * { flex: 1; min-width: 0; }

      @media (max-width: 1100px) {
        .app-grid {
          grid-template-columns: 1fr;
          grid-auto-rows: minmax(320px, auto);
          overflow-y: auto;
        }
      }

      .app-foot {
        padding: var(--space-2) var(--space-4);
        border-top: 2px solid var(--border);
        background: var(--bg-2);
        font-size: 11px;
        color: var(--fg-2);
      }
      .app-foot code {
        background: var(--bg-1);
        border: 1px solid var(--border-soft);
        padding: 1px 6px;
        border-radius: 3px;
        color: var(--fg-1);
      }
    `}</style>
  );
}
